import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { State } from 'vuex-class'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Quote } from '@/modules/Quote/interfaces/quote'
import PolicyDetailDetail from '@/modules/Quote/views/detail.vue'

import { showErrors, showNotification } from '@/utils/snack'
import { ApolloResponse } from '@/interfaces/apollo'
import { dateTimeToDisplay, dateToDisplay, dateToNow } from '@/utils/dateTime'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppTabs from '@/components/AppTabs.vue'
import AppDialog from '@/components/AppDialog.vue'
import AppTextSearch from '@/components/AppTextSearch.vue'
import AppCheckbox from '@/components/AppCheckbox.vue'
import AppRadio from '@/components/AppRadio.vue'
import AcordFormDialog from '@/components/AcordFormDialog.vue'
import AssignToDialog from '@/components/AssignToDialog.vue'
import { RecordType } from '@/modules/RecordType/interfaces/record-type'
import RecordTypesHandler from '@/mixins/record-types'
import QuoteDialog from './quote-dialog'
import { getQuote, updateQuoteLabels, assignQuote } from '@/modules/Quote/gql/quote.gql'
import { getLabels } from '@/modules/Label/gql/label-gql'
import { quoteStatus, quoteStatusClass, quoteType } from '@/modules/Quote/helpers/quote'
import { Label } from '@/modules/Label/interfaces/label'
import { SnackType } from '@/interfaces/snack'
import QuoteForm from '@/modules/Quote/components/QuoteForm.vue'
import { dispatchSetIsLoading } from '../../../store/dispatches'

@Component({
  components: {
    AppBreadcrumbs,
    PolicyDetailDetail,
    AppTabs,
    AppDialog,
    AppTextSearch,
    AppCheckbox,
    AppRadio,
    AcordFormDialog,
    AssignToDialog,
    QuoteForm
  }
})

export default class QuoteDetail extends mixins(QuoteDialog, RecordTypesHandler) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Insurance Cases'
    },
    {
      text: 'Quotes',
      to: { name: 'QuotesList' }
    }
  ]

  quote: Quote | null = null

  tabActive = 0
  tabItems = [
    {
      title: 'Details',
      route: 'QuoteDetailDetail'
    },
    {
      title: 'ACORD Form',
      route: 'QuoteAcordForm'
    },
    {
      title: 'Related',
      route: 'QuoteDetailRelated'
    },
    {
      title: 'Source',
      route: 'QuoteDetailSource'
    },
    {
      title: 'Additional',
      route: 'QuoteDetailAdditional'
    }
  ]

  disableConfirm = false

  recordTypeIndex = 0
  activeAssign = false
  activeDelete = false

  initState = true

  quoteStatus = quoteStatus

  acordFormDialog = {
    show: false
  }

  quoteStatusClass = quoteStatusClass
  quoteType = quoteType

  showLabelOptions = false

  labels: Label[] = []

  quoteLabels: Label[] = []

  dateToNow = dateToNow

  @State(state => state.app.isFold) isFold!: boolean

  get tabTitles () {
    return this.tabItems.map(item => item.title)
  }

  get recordType () {
    if (this.$R.isEmpty(this.recordTypes) || !this.quote) {
      return null
    }
    const recordTypeName = this.quote.recordType.name
    return this.$R.find((recordType: RecordType) => {
      return recordType.name === recordTypeName
    })(this.recordTypes)
  }

  dateTimeToDisplay = dateTimeToDisplay
  dateToDisplay = dateToDisplay

  async mounted () {
    const { name } = this.$route
    const index = this.$R.findIndex((item: any) => item.route === name)(this.tabItems)
    this.tabActive = index
    await this.getLabels()
    this.getQuote()
    await this.getRecordTypes('POLICY')
  }

  @Watch('$route.params.id')
  async onRouteParamUpdate (val: string) {
    await this.getLabels()
    this.getQuote()
  }

  updateQuote (step: number) {
    this.showQuoteDialog(step, this.quote as Quote)
  }

  async getLabels () {
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getLabels,
        variables: {
          type: 'QUOTE'
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.labels = result.data.getLabels
      this.$R.forEach((label: Label) => {
        label.checked = false
      }, this.labels)
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async assignQuotePost (userIds: number[]) {
    if (!this.targetQuote) {
      return
    }
    const { id } = this.targetQuote
    this.hideAssignDialog()
    try {
      const result = await this.$apollo.mutate({
        mutation: assignQuote,
        variables: {
          quoteId: Number(id),
          userIds
        }
      })

      const res = result.data.assignQuote
      if (res) {
        showNotification({
          msg: 'Assign quote to staff successfully',
          type: SnackType.Success
        })
      }
    } catch (error) {
      showErrors(error.graphQLErrors)
    }
  }

  async assignQuoteConfirm (userIds: number[]) {
    await this.assignQuotePost(userIds)
    this.getQuote()
  }

  async assignQuoteCancel () {
    this.assignDialog.show = false
  }

  async getQuote () {
    const quoteId = Number(this.$route.params.id)

    dispatchSetIsLoading(true)
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getQuote,
        variables: {
          quoteId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.quote = result.data.getQuote as Quote

      const that = this
      this.quoteLabels = JSON.parse(JSON.stringify(this.quote.labels))
      if (this.quoteLabels.length > 0) {
        this.$R.forEach((label: Label) => {
          that.$R.forEach((labelItem: Label) => {
            if (Number(label.id) === Number(labelItem.id)) {
              label.checked = true
            }
          }, that.quoteLabels)
        }, this.labels)
      }

      const caseId = this.quote.caseId || '--'

      if (this.initState) {
        this.breadcrumbs.push({
          text: `${this.quote.name} (Case ID: ${caseId})`
        })
        this.initState = false
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    } finally {
      dispatchSetIsLoading(false)
    }
  }

  clickTabHandler (index: number) {
    const item = this.tabItems[index]
    if (item.route && item.route !== this.$route.name) {
      this.$router.push({
        name: item.route
      })
    }
  }

  async deleteQuoteConfirm () {
    await this.deleteQuotePost()
    this.$router.replace({
      name: 'QuotesList'
    })
  }

  showAcordFormDialog (e: Event, targetQuote: Quote) {
    e.preventDefault()
    e.stopPropagation()

    this.targetQuote = targetQuote
    this.acordFormDialog.show = true
  }

  hideAcordFormDialog () {
    this.targetQuote = null
    this.acordFormDialog.show = false
  }

  routeToAccountDetail () {
    if (this.quote && this.quote.account) {
      this.$router.push({
        name: 'AccountDetailDetail',
        params: {
          id: this.quote.account.id + ''
        }
      })
    }
  }

  routeToContactDetail () {
    if (this.quote && this.quote.primaryContact) {
      this.$router.push({
        name: 'ContactDetailDetail',
        params: {
          id: this.quote.primaryContact.id + ''
        }
      })
    }
  }

  checkLabel (val: Label) {
    if (!this.quote) {
      return
    }
    const valIndex = this.quoteLabels.findIndex((label: Label) => {
      return label.content === val.content
    })
    if (val.checked) {
      if (this.quoteLabels.length > 0) {
        this.quoteLabels.splice(valIndex, 1)
      }
    } else {
      this.quoteLabels.push(val)
    }
    val.checked = !val.checked
  }

  async hideLabelOption () {
    this.showLabelOptions = false
    if (!this.quote) {
      return
    }
    const labelIds = this.quote.labels.map((label: { id: any }) => Number(label.id))
    const policyLabelIds = this.quoteLabels.map((label: { id: any }) => Number(label.id))
    var isSame = labelIds.length === policyLabelIds.length && labelIds.sort().toString() === policyLabelIds.sort().toString()
    if (!isSame) {
      await this.editPolicyLabel()
    }
  }

  async editPolicyLabel () {
    const { quoteLabels, quote } = this
    if (!quote) {
      return
    }
    const { id } = quote

    const labelIds = quoteLabels.map((label: { id: any }) => Number(label.id))

    try {
      const res = await this.$apollo.mutate({
        mutation: updateQuoteLabels,
        variables: {
          labelIds,
          quoteId: Number(id)
        }
      })
      showNotification({
        msg: 'Update the quote label successfully',
        type: SnackType.Success
      })
      this.getQuote()
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  routeToLabelList () {
    this.$router.push({
      name: 'LabelListType',
      params: {
        type: 'POLICY'
      }
    })
  }
}
